import Popover from '@mui/material/Popover'
import { type ReactNode,useId, useMemo, useRef, useState } from 'react'

import {
  colors,
  componentTokens,
  shadows
} from '../KestrixDesignSystem.jsx'
import { OptionsMenu } from '../KestrixDropdown/components/OptionsMenu.tsx'
import { DROPDOWN_OVERLAY, embeddedOptionsMenuStyle, FONT_SIZE } from '../KestrixDropdown/styles.ts'
import type { DropdownOption } from '../KestrixDropdown/types.ts'
import {
  KestrixCopyViewLinkIcon,
  KestrixDeleteViewIcon,
  KestrixDuplicateViewIcon,
  KestrixEditViewDescriptionIcon,
  KestrixRenameViewIcon
} from '../KestrixSvgIcons.jsx'
import { useViewTabPopoverTransition } from './hooks/useViewTabPopoverTransition.ts'
import {
  viewTabPopoverAnchorOrigin,
  viewTabPopoverTransformOrigin
} from './view-tab-popover-origin.ts'
import { viewTabTokens as tokens } from './view-tab-tokens.ts'
import type { ViewTabMenuCallbacks } from './ViewTab.types.ts'

export interface ViewTabContextMenuProps extends ViewTabMenuCallbacks {
  open: boolean
  /** Element (or lazy getter) the menu panel anchors to. */
  anchorEl: HTMLElement | (() => HTMLElement | null) | null
  onClose: () => void
  locked?: boolean
}

type ViewTabMenuAction = 'rename' | 'edit-description' | 'duplicate' | 'copy-link' | 'delete'

const overlayPaperSx = {
  mt: `${DROPDOWN_OVERLAY.menuGap}px`,
  minWidth: tokens.sizing.menuMinWidth,
  padding: `${DROPDOWN_OVERLAY.menuPadding}px`,
  borderRadius: componentTokens.input.borderRadius,
  border: `1px solid ${colors.border.light}`,
  background: colors.background.secondary,
  boxShadow: shadows.dropdown,
  overflow: 'hidden'
}

const iconOption = (
  value: ViewTabMenuAction,
  label: string,
  leadingIcon: ReactNode,
  extra?: { readonly tone?: 'danger' }
): DropdownOption => ({
  type: 'text-with-icon',
  value,
  label,
  leadingIcon,
  testId: `view-tab-menu-${value}`,
  ...extra
})

const buildViewTabMenuOptions = (
  locked: boolean,
  includeDelete: boolean
): DropdownOption[] => {
  const options: DropdownOption[] = []
  if (!locked) {
    options.push(
      iconOption('rename', 'Rename view', <KestrixRenameViewIcon />),
      iconOption(
        'edit-description',
        'Edit view description',
        <KestrixEditViewDescriptionIcon />
      )
    )
  }
  options.push(
    iconOption('duplicate', 'Duplicate view', <KestrixDuplicateViewIcon />),
    iconOption('copy-link', 'Copy link to view', <KestrixCopyViewLinkIcon />)
  )
  if (includeDelete) {
    options.push(
      { type: 'divider', value: 'divider' },
      iconOption('delete', 'Delete View', <KestrixDeleteViewIcon />, {
        tone: 'danger'
      })
    )
  }
  return options
}

const resolveAnchorEl = (
  anchorEl: ViewTabContextMenuProps['anchorEl']
): HTMLElement | null => {
  if (anchorEl == null) {
    return null
  }
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl
}

export const ViewTabContextMenu = ({
  open,
  anchorEl,
  onClose,
  locked = false,
  onRenameView,
  onEditDescription,
  onDuplicate,
  onCopyLink,
  onDelete
}: ViewTabContextMenuProps) => {
  const listboxId = useId()
  const listboxRef = useRef<HTMLUListElement | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
  const popoverTransition = useViewTabPopoverTransition()
  const includeDelete = onDelete != null
  const options = useMemo(
    () => buildViewTabMenuOptions(locked, includeDelete),
    [includeDelete, locked]
  )

  const runAction = (value: string): void => {
    onClose()
    const actions: Record<ViewTabMenuAction, (() => void) | undefined> = {
      rename: onRenameView,
      'edit-description': onEditDescription,
      duplicate: onDuplicate,
      'copy-link': onCopyLink,
      delete: onDelete
    }
    actions[value as ViewTabMenuAction]?.()
  }

  return (
    <Popover
      open={open}
      anchorEl={resolveAnchorEl(anchorEl)}
      onClose={onClose}
      anchorOrigin={viewTabPopoverAnchorOrigin}
      transformOrigin={viewTabPopoverTransformOrigin}
      elevation={0}
      TransitionComponent={popoverTransition.TransitionComponent}
      transitionDuration={popoverTransition.transitionDuration}
      data-testid="view-tab-menu"
      slotProps={{
        backdrop: { sx: { backgroundColor: 'transparent' } },
        paper: { sx: overlayPaperSx }
      }}
    >
      <OptionsMenu
        listboxId={listboxId}
        listboxRef={listboxRef}
        menuStyle={embeddedOptionsMenuStyle}
        fontSize={FONT_SIZE.small}
        selectionMode="single"
        value=""
        optionSpacing="compact"
        filteredOptions={options}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        selectOption={(option) => runAction(String(option.value))}
      />
    </Popover>
  )
}
